import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import cn from "classnames";
import { KeyboardArrowDownRounded } from "@mui/icons-material";
import styles from "./Sidebar.module.scss";
import { SubMenuItem } from "Shared/Types/sidebar";
import SidebarDisclosure from "./SidebarDisclosure";
import {
  PeopleOutlineRounded,
  ContactPageOutlined,
  HistoryRounded,
  DescriptionOutlined,
  ErrorOutlineRounded,
  CalendarMonthOutlined,
  WarningAmberRounded,
  BuildOutlined,
  ChecklistRounded,
  AssignmentOutlined,
  BadgeOutlined,
  DirectionsCarOutlined,
  NotificationsNoneRounded,
  Inventory2Outlined,
  ShoppingCartOutlined,
} from "@mui/icons-material";

const childIcons: Record<string, React.ElementType> = {
  Users: PeopleOutlineRounded,
  Contact: ContactPageOutlined,
  History: HistoryRounded,
  Forms: DescriptionOutlined,
  "Item Failures": ErrorOutlineRounded,
  Schedules: CalendarMonthOutlined,
  Issue: WarningAmberRounded,
  Faults: ErrorOutlineRounded,
  "Service history": HistoryRounded,
  "Service tasks": ChecklistRounded,
  "Work orders": AssignmentOutlined,
  "Contact Renewal": BadgeOutlined,
  "Vehicle Renewal": DirectionsCarOutlined,
  "Service Reminders": NotificationsNoneRounded,
  Parts: Inventory2Outlined,
  "Purchase Orders": ShoppingCartOutlined,
};

export type SidebarItemProps = {
  icon: React.ElementType;
  title: string;
  url: string;
  subMenu?: SubMenuItem[];
  isDropdown?: boolean;
  withNotificaion?: boolean;
  notifcationsCount?: number;
  searching?: boolean;
};

const matches = (path: string, url: string) =>
  path === url || path.startsWith(url + "/");

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  title,
  url,
  subMenu = [],
  searching = false,
}) => {
  const { pathname } = useLocation();
  const active =
    matches(pathname, url) ||
    subMenu.some((item) => matches(pathname, item.url));
  const [open, setOpen] = useState(active);
  const visible = searching || open;

  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  const iconNode = (
    <span className={styles.sidebarItem__icon}>
      {React.createElement(icon, { "aria-hidden": true })}
    </span>
  );

  if (!subMenu.length) {
    return (
      <Link
        className={cn(styles.sidebarItem, active && styles.sidebarItem_active)}
        to={url}
        aria-current={active ? "page" : undefined}
      >
        {iconNode}
        <span className={styles.sidebarItem__title}>{title}</span>
      </Link>
    );
  }

  return (
    <div className={cn(styles.sidebarItem__group)}>
      <button
        type="button"
        className={cn(styles.sidebarItem, active && styles.sidebarItem_active)}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={visible}
      >
        {iconNode}
        <span className={styles.sidebarItem__title}>{title}</span>
        <KeyboardArrowDownRounded
          className={styles.sidebarItem__chevron}
          aria-hidden="true"
        />
      </button>
      <SidebarDisclosure open={visible}>
        <div className={styles.sidebarItem__children}>
          {subMenu.map((item) => {
            const selected = matches(pathname, item.url);
            return (
              <Link
                key={item.url}
                className={cn(
                  styles.sidebarItem__child,
                  selected && styles.sidebarItem__child_active,
                )}
                to={item.url}
                aria-current={selected ? "page" : undefined}
              >
                <span
                  className={styles.sidebarItem__childIcon}
                  aria-hidden="true"
                >
                  {React.createElement(childIcons[item.title] || BuildOutlined)}
                </span>
                <span>{item.title}</span>
              </Link>
            );
          })}
        </div>
      </SidebarDisclosure>
    </div>
  );
};

export default SidebarItem;
